import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, X as XIcon } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { InvoiceCatalogItem, InvoiceLanguage } from '../../../services/apiService';
import { fmtMoneyPlain } from './invoiceHelpers';

interface CatalogPickerProps {
    open: boolean;
    onClose: () => void;
    onPick: (item: InvoiceCatalogItem) => void;
    language: InvoiceLanguage;
}

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const CATEGORY_KEYS: Record<string, string> = {
    implantology: 'billing.category.implantology',
    prophylaxis: 'billing.category.prophylaxis',
    aesthetic: 'billing.category.aesthetic',
    oral_surgery: 'billing.category.oral_surgery',
    orthodontics: 'billing.category.orthodonticsLong',
    consultation: 'billing.category.consultation',
    other: 'billing.category.other',
};

const CatalogPicker: React.FC<CatalogPickerProps> = ({ open, onClose, onPick, language }) => {
    const { t } = useTranslation();
    const [items, setItems] = useState<InvoiceCatalogItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [category, setCategory] = useState('');

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                setLoading(true);
                const res = await apiService.listInvoiceCatalog({ activeOnly: true });
                setItems(res.data.data || []);
            } catch {
                setItems([]);
            } finally {
                setLoading(false);
            }
        })();
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return items.filter(it => {
            if (category && it.category !== category) return false;
            if (!q) return true;
            const hay = [
                it.code,
                it.name?.de, it.name?.es, it.name?.en,
                it.description?.de, it.description?.es, it.description?.en,
            ].filter(Boolean).join(' ').toLowerCase();
            return hay.includes(q);
        });
    }, [items, query, category]);

    if (!open) return null;

    return (
        <div
            className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4 bg-black/40"
            onClick={onClose}
        >
            <div
                className="w-full max-w-2xl rounded-xl bg-brand-surface border border-brand-border shadow-xl flex flex-col max-h-[80vh]"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between px-5 py-3 border-b border-brand-border">
                    <h3 className="text-sm font-semibold text-brand-text">{t('billing.picker.title')}</h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer"
                        aria-label={t('common.close')}
                    >
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                <div className="px-5 py-3 border-b border-brand-border grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="sm:col-span-2 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                        <input
                            type="text"
                            autoFocus
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={t('billing.picker.searchPlaceholder')}
                            className={`${inputClass} pl-9`}
                        />
                    </div>
                    <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClass}>
                        <option value="">{t('billing.picker.categoryAll')}</option>
                        {Object.entries(CATEGORY_KEYS).map(([k, v]) => (
                            <option key={k} value={k}>{t(v)}</option>
                        ))}
                    </select>
                </div>

                <div className="overflow-y-auto flex-1">
                    {loading ? (
                        <div className="p-8 text-center text-sm text-brand-text-muted">{t('billing.picker.loading')}</div>
                    ) : filtered.length === 0 ? (
                        <div className="p-8 text-center text-sm text-brand-text-muted">
                            {items.length === 0
                                ? t('billing.picker.empty')
                                : t('billing.picker.noMatches')}
                        </div>
                    ) : (
                        <ul className="divide-y divide-brand-border">
                            {filtered.map((it) => {
                                const name = it.name[language] || it.name.de || it.name.es || it.name.en;
                                return (
                                    <li key={it.id}>
                                        <button
                                            type="button"
                                            onClick={() => onPick(it)}
                                            className="w-full text-left px-5 py-3 hover:bg-brand-surface-2/60 transition-colors cursor-pointer flex items-center justify-between gap-3"
                                        >
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-brand-text truncate">
                                                    {name || <span className="italic text-brand-text-muted">{t('billing.picker.unnamed')}</span>}
                                                </div>
                                                <div className="text-xs text-brand-text-muted truncate">
                                                    {it.code && <span className="tabular-nums">{it.code}</span>}
                                                    {it.code && ' · '}
                                                    {CATEGORY_KEYS[it.category] ? t(CATEGORY_KEYS[it.category]) : it.category}
                                                    {' · '}{t('billing.picker.vatShort', { rate: it.default_vat_rate })}
                                                </div>
                                            </div>
                                            <div className="text-sm font-semibold text-brand-text tabular-nums shrink-0">
                                                {fmtMoneyPlain(it.default_price_cents)} €
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CatalogPicker;
